import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { PhotoThumb, PhotoViewer } from '@indipia/ui-core'
import { useBlobUrls } from '../../hooks/useBlobUrls'

// 진행 내역 팝업용 고객 요청 사진 줄 — 썸네일(PhotoThumb)을 나열하고 누르면 원본을 PhotoViewer 로 본다. 읽기 전용.
// 썸네일은 보이는 즉시, 원본은 눌렀을 때만 받는다. 부모 Modal 이 뷰어가 열린 동안 닫히지 않도록 열림 상태를 알린다.

export interface StripPhoto { id: number; thumbUrl: string; url: string }

interface Props {
  photos: StripPhoto[]
  /** 사진 대체 설명 접두(예: '고객 요청 사진') */
  alt: string
  onViewerOpenChange?: (open: boolean) => void
}

export function CustReqPhotoStrip({ photos, alt, onViewerOpenChange }: Props) {
  const thumbs = useBlobUrls(photos.map(p => p.thumbUrl), photos.length > 0)
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)
  const originals = useBlobUrls(photos.map(p => p.url), viewerIndex !== null)
  const notifyRef = useRef(onViewerOpenChange)
  notifyRef.current = onViewerOpenChange

  const viewerItems = (originals ?? [])
    .map((src, i) => (src ? { id: photos[i].id, src, alt: `${alt} ${i + 1}` } : null))
    .filter((v): v is { id: number; src: string; alt: string } => v !== null)
  const allFailed = viewerIndex !== null && originals !== null && viewerItems.length === 0
  useEffect(() => {
    if (!allFailed) return
    toast.error('원본 사진을 불러오지 못했어요')
    setViewerIndex(null)
  }, [allFailed])

  const showViewer = viewerIndex !== null && originals !== null && viewerItems.length > 0
  useEffect(() => { notifyRef.current?.(showViewer) }, [showViewer])

  if (photos.length === 0) return null
  const start = viewerIndex === null ? 0 : Math.max(0, viewerItems.findIndex(v => v.id === photos[viewerIndex]?.id))

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <PhotoThumb
            key={p.id}
            size="sm"
            src={thumbs?.[i] ?? null}
            loading={thumbs === null}
            alt={`${alt} ${i + 1}`}
            onOpen={() => setViewerIndex(i)}
          />
        ))}
      </div>
      {showViewer && (
        <PhotoViewer
          open
          onClose={() => setViewerIndex(null)}
          items={viewerItems}
          index={start}
          onIndexChange={i => setViewerIndex(photos.findIndex(p => p.id === viewerItems[i]?.id))}
          title={alt}
        />
      )}
    </>
  )
}
